import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Upload, X, CheckCircle2, Loader2, PhoneCall, Flame,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import SeoHead from "../components/SeoHead";
import { whatsappLink } from "../data/content";
import { LOCALITIES } from "../data/services";
import { useServices } from "../context/ServicesContext";
import { api, uploadFile, formatApiErrorDetail } from "../lib/api";

const STEP_LABELS = ["Profile", "Expertise", "Areas", "Verify"];
const EXPERIENCE = ["0-2 years", "2-5 years", "5-10 years", "10-20 years", "20+ years"];

export default function PanditRegisterPage() {
  const navigate = useNavigate();
  const { pandit } = useServices();
  const languageOptions = pandit?.languages || ["Hindi", "Marathi", "Sanskrit", "Gujarati", "English", "Bengali", "Tamil", "Telugu", "Kannada"];

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);

  const [form, setForm] = useState({
    name: "", mobile: "", email: "", experience: "",
    languages: [], specializations: "", expected_charges: "", availability: "",
    service_areas: [], address: "", kyc: [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (key, val) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((a) => a !== val) : [...f[key], val] }));

  const canNext = useMemo(() => {
    if (step === 0) return form.name.trim() && /^[0-9+\-\s]{10,15}$/.test(form.mobile) && form.experience;
    if (step === 1) return form.languages.length > 0 && form.specializations.trim().length > 2;
    if (step === 2) return form.service_areas.length > 0;
    if (step === 3) return form.kyc.length > 0;
    return false;
  }, [step, form]);

  const handleKyc = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await uploadFile(file);
        setForm((f) => ({ ...f, kyc: [...f.kyc, { id: res.id, filename: res.filename }] }));
      }
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/partners", {
        name: form.name, mobile: form.mobile, email: form.email,
        service_category: "Pandit Ji / Puja Services",
        experience: form.experience,
        service_areas: form.service_areas,
        kyc_doc_ids: form.kyc.map((k) => k.id),
        type: "pandit",
        languages: form.languages,
        specializations: form.specializations,
        address: form.address,
        expected_charges: form.expected_charges,
        availability: form.availability,
      });
      setDone(data.partner);
      toast.success("Application submitted!");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <SeoHead title="Pandit Ji Application Submitted - ApnaFix" />
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center af-fade-up" data-testid="pandit-register-confirmation">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-amber-50 text-amber-600 mx-auto"><CheckCircle2 className="w-9 h-9" /></span>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">Pranam, {done.name.split(" ")[0]}!</h1>
          <p className="mt-2 text-slate-600">Your Pandit Ji registration is submitted for verification. Our team will review your details and get in touch shortly.</p>
          <div className="mt-6 text-left bg-slate-50 rounded-2xl p-5 space-y-2 text-sm">
            <p className="flex justify-between"><span className="text-slate-500">Reference ID</span><span className="font-mono-num font-semibold text-slate-900">{String(done.id).slice(0, 8).toUpperCase()}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Role</span><span className="font-medium text-slate-900">Pandit Ji</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-medium text-amber-600">Pending verification</span></p>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={whatsappLink(`Hi ApnaFix, I submitted my Pandit Ji registration (ID ${String(done.id).slice(0,8).toUpperCase()}).`)} target="_blank" rel="noreferrer"
               className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 px-5"><PhoneCall className="w-4 h-4" /> Chat on WhatsApp</a>
            <Button variant="outline" onClick={() => navigate("/")} className="h-11 px-5 font-semibold">Back to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SeoHead title="Register as a Pandit Ji - ApnaFix" description="Join ApnaFix as a verified Pandit Ji. Get puja bookings for Griha Pravesh, Satyanarayan, Havan and more in your area." />
      {/* Hero strip */}
      <div className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <Link to="/hire-pandit" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white mb-3"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <span className="inline-flex items-center gap-2 rounded-full bg-amber-400/15 border border-amber-300/20 px-3 py-1.5 text-xs font-semibold text-amber-200"><Flame className="w-3.5 h-3.5" /> For Pandit Ji</span>
          <h1 className="mt-3 font-display text-2xl sm:text-4xl font-extrabold tracking-tight">Register as a Pandit Ji</h1>
          <p className="mt-2 text-slate-300 max-w-xl">Receive local puja bookings, manage your schedule and grow your reach — all from one platform.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Stepper */}
        <div className="flex items-center gap-1 sm:gap-2">
          {STEP_LABELS.map((lbl, i) => (
            <div key={lbl} className="flex-1 flex items-center gap-1 sm:gap-2">
              <div className={`flex items-center gap-2 ${i <= step ? "text-navy" : "text-slate-400"}`}>
                <span className={`grid place-items-center w-7 h-7 rounded-full text-xs font-bold ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-400"}`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</span>
                <span className="hidden sm:inline text-xs font-semibold">{lbl}</span>
              </div>
              {i < STEP_LABELS.length - 1 && <div className={`flex-1 h-0.5 rounded ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-8 min-h-[340px]">
          {step === 0 && (
            <div className="af-fade-up space-y-4">
              <h2 className="font-display font-bold text-lg text-slate-900">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sm">Full name</Label><Input data-testid="pandit-reg-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11" placeholder="Pandit ji's name" /></div>
                <div><Label className="text-sm">Mobile number</Label><Input data-testid="pandit-reg-mobile-input" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="mt-1.5 h-11" placeholder="10-digit mobile" /></div>
              </div>
              <div><Label className="text-sm">Email (optional)</Label><Input data-testid="pandit-reg-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11" placeholder="you@email.com" /></div>
              <div>
                <Label className="text-sm">Experience</Label>
                <Select value={form.experience} onValueChange={(v) => set("experience", v)}>
                  <SelectTrigger data-testid="pandit-reg-experience-select" className="mt-1.5 h-11"><SelectValue placeholder="Years of experience" /></SelectTrigger>
                  <SelectContent>{EXPERIENCE.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="af-fade-up space-y-5">
              <h2 className="font-display font-bold text-lg text-slate-900">Your expertise</h2>
              <div>
                <Label className="text-sm">Languages you perform puja in</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {languageOptions.map((l) => {
                    const active = form.languages.includes(l);
                    return (
                      <button key={l} data-testid={`pandit-reg-lang-${l.toLowerCase()}`} onClick={() => toggle("languages", l)}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${active ? "border-brand-orange bg-orange-50 text-orange-900 ring-1 ring-orange-200" : "border-slate-200 text-slate-700 hover:border-orange-300"}`}>
                        {active && <Check className="w-3.5 h-3.5" />} {l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div><Label className="text-sm">Specializations (puja types)</Label><Textarea data-testid="pandit-reg-specializations-input" value={form.specializations} onChange={(e) => set("specializations", e.target.value)} className="mt-1.5" placeholder="e.g. Griha Pravesh, Satyanarayan Katha, Havan, Vastu Shanti" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sm">Expected charges (optional)</Label><Input data-testid="pandit-reg-charges-input" value={form.expected_charges} onChange={(e) => set("expected_charges", e.target.value)} className="mt-1.5 h-11" placeholder="e.g. ₹2100 onwards per puja" /></div>
                <div><Label className="text-sm">Availability (optional)</Label><Input data-testid="pandit-reg-availability-input" value={form.availability} onChange={(e) => set("availability", e.target.value)} className="mt-1.5 h-11" placeholder="e.g. Mornings, weekends" /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="af-fade-up space-y-4">
              <h2 className="font-display font-bold text-lg text-slate-900 mb-1">Where can you serve?</h2>
              <p className="text-sm text-slate-500">Select the localities you can travel to for pujas.</p>
              <div className="grid grid-cols-2 gap-3">
                {LOCALITIES.map((l) => {
                  const active = form.service_areas.includes(l.name);
                  return (
                    <button key={l.id} data-testid={`pandit-reg-area-${l.slug}`} onClick={() => toggle("service_areas", l.name)}
                            className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${active ? "border-brand-orange bg-orange-50 ring-2 ring-orange-200" : "border-slate-200 hover:border-orange-300"}`}>
                      <span className={`grid place-items-center w-5 h-5 rounded border shrink-0 ${active ? "bg-brand-orange border-brand-orange text-white" : "border-slate-300 bg-white"}`}>{active && <Check className="w-3.5 h-3.5" />}</span>
                      <div><p className="font-semibold text-slate-900">{l.name}</p><p className="text-xs text-slate-500">{l.societies}</p></div>
                    </button>
                  );
                })}
              </div>
              <div><Label className="text-sm">Your address / area (optional)</Label><Input data-testid="pandit-reg-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5 h-11" placeholder="e.g. Mahavir Nagar, Kandivali West" /></div>
            </div>
          )}

          {step === 3 && (
            <div className="af-fade-up space-y-4">
              <h2 className="font-display font-bold text-lg text-slate-900">ID verification</h2>
              <p className="text-sm text-slate-500">Upload your Aadhaar / ID proof (and any certification). We verify every Pandit Ji before activation.</p>
              <div className="flex flex-wrap gap-3">
                {form.kyc.map((k) => (
                  <div key={k.id} className="relative w-24 h-24 rounded-xl bg-slate-100 border border-slate-200 grid place-items-center text-[10px] text-slate-500 p-2 text-center overflow-hidden">
                    <span className="line-clamp-3">{k.filename}</span>
                    <button onClick={() => set("kyc", form.kyc.filter((x) => x.id !== k.id))} className="absolute -top-1.5 -right-1.5 w-5 h-5 grid place-items-center rounded-full bg-red-500 text-white"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label data-testid="pandit-reg-kyc-upload" className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 grid place-items-center cursor-pointer hover:border-brand-orange text-slate-400 hover:text-brand-orange">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleKyc} />
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="font-semibold"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          {step < 3 ? (
            <Button data-testid="pandit-reg-next-btn" disabled={!canNext} onClick={() => setStep((s) => s + 1)} className="bg-brand-orange hover:bg-orange-600 text-white font-semibold px-6">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button data-testid="pandit-reg-submit-btn" disabled={!canNext || submitting} onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting...</> : <>Submit for Verification <Check className="w-4 h-4 ml-1" /></>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
